<template>
    <div class="card border-0 shadow-sm mb-5">
        <div class="card-body">
            <h3 class="h5 mb-3">{{ $t("warehouses.quickEntry") }}</h3>

            <div class="row g-2 mb-3">
                <div v-for="m in modes" :key="m.key" class="col-6 col-md-3">
                    <button type="button" class="btn w-100 py-2"
                        :class="mode === m.key ? 'btn-' + m.color + ' text-white' : 'btn-outline-' + m.color"
                        @click="setMode(m.key)">
                        {{ $t("warehouses." + m.key) }}
                    </button>
                </div>
            </div>
            <p :class="mode === 'issue' ? 'text-danger' : 'text-muted'" class="small mb-3">
                {{ $t("warehouses.modeHelp." + mode) }}
            </p>

            <!-- Příjem / Výdej / Inventura -->
            <form v-if="mode !== 'conversion'" @submit.prevent="submitStock">
                <div class="row g-3 align-items-end">
                    <div class="col-12 col-lg-5">
                        <label class="form-label">{{ $t("products.selectProduct") }}</label>
                        <select v-model="stockForm.productId" class="form-select" required>
                            <option v-for="item in stock" :key="item.product.id" :value="item.product.id">
                                {{ item.product.shorten_name }} – {{ item.product.name }}
                                ({{ formatAmount(item.amount) }} {{ item.product.measurement_unit }})
                            </option>
                        </select>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-2">
                        <label class="form-label">
                            {{ mode === "inventory" ? $t("warehouses.actualAmount") : $t("warehouses.amount") }}
                        </label>
                        <div class="input-group">
                            <input v-model="stockForm.amount" type="text" inputmode="decimal" autocomplete="off"
                                class="form-control" required />
                            <span v-if="selectedStock" class="input-group-text">
                                {{ selectedStock.product.measurement_unit }}
                            </span>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <label class="form-label">{{ $t("warehouses.note") }}</label>
                        <input v-model="stockForm.note" type="text" maxlength="256" class="form-control"
                            :placeholder="$t('warehouses.optional')" />
                    </div>
                    <div class="col-12 col-lg-2">
                        <button type="submit" class="btn w-100 py-2 text-white" :class="'btn-' + currentMode.color"
                            :disabled="!stockValid || saving">
                            {{ $t("warehouses.action." + mode) }}
                        </button>
                    </div>
                    <div v-if="selectedStock && stockValid" class="col-12 small">
                        <span v-if="mode === 'inventory'">
                            {{ $t("warehouses.inventoryPreview", {
                                current: formatAmount(selectedStock.amount),
                                diff: signed(stockAmount - selectedStock.amount),
                                unit: selectedStock.product.measurement_unit
                            }) }}
                        </span>
                        <span v-else :class="{ 'text-danger fw-bold': newAmount < 0 }">
                            {{ $t("warehouses.newAmountPreview", {
                                amount: formatAmount(newAmount),
                                unit: selectedStock.product.measurement_unit
                            }) }}
                        </span>
                    </div>
                </div>
            </form>

            <!-- Zpracování (přepočet) -->
            <form v-else @submit.prevent="submitConversion">
                <div v-if="conversions.length === 0" class="text-muted">
                    {{ $t("warehouses.noConversions") }}
                </div>
                <div v-else class="row g-3 align-items-end">
                    <div class="col-12 col-lg-5">
                        <label class="form-label">{{ $t("warehouses.whatProduced") }}</label>
                        <select v-model="conversionForm.outputId" class="form-select" required>
                            <option v-for="c in conversions" :key="c.id" :value="c.output_product">
                                {{ c.source.shorten_name }} → {{ c.output.shorten_name }} ({{ c.output.name }})
                            </option>
                        </select>
                    </div>
                    <div class="col-12 col-lg-3">
                        <label class="form-label">{{ $t("warehouses.producedAmount") }}</label>
                        <div class="input-group">
                            <input v-model="conversionForm.count" type="text" inputmode="decimal" autocomplete="off"
                                class="form-control" required />
                            <span v-if="selectedConversion" class="input-group-text">
                                {{ selectedConversion.output.measurement_unit }}
                            </span>
                        </div>
                    </div>
                    <div class="col-12 col-lg-2 offset-lg-2">
                        <button type="submit" class="btn btn-primary w-100 py-2 text-white"
                            :disabled="!conversionValid || saving">
                            {{ $t("warehouses.action.conversion") }}
                        </button>
                    </div>
                    <div v-if="selectedConversion" class="col-12 small">
                        <div class="text-muted">
                            {{ $t("products.conversionRule", {
                                source: selectedConversion.source.shorten_name,
                                sourceUnit: selectedConversion.source.measurement_unit,
                                output: selectedConversion.output.shorten_name,
                                outputUnit: selectedConversion.output.measurement_unit,
                                coef: formatAmount(selectedConversion.coeficient)
                            }) }}
                        </div>
                        <div v-if="conversionValid" :class="{ 'text-danger fw-bold': sourceAfter < 0 }">
                            {{ $t("warehouses.willConsume", {
                                count: formatAmount(consumedAmount),
                                unit: selectedConversion.source.measurement_unit,
                                product: selectedConversion.source.shorten_name,
                                rest: formatAmount(sourceAfter)
                            }) }}
                        </div>
                    </div>
                </div>
            </form>

            <div v-if="message" class="alert mt-3 mb-0" :class="error ? 'alert-danger' : 'alert-success'" role="alert">
                {{ message }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        warehouseId: { type: [String, Number], required: true },
        stock: { type: Array, default: () => [] },
        conversions: { type: Array, default: () => [] },
    },
    emits: ["updated"],
    data() {
        return {
            modes: [
                { key: "receipt", color: "success" },
                { key: "issue", color: "danger" },
                { key: "conversion", color: "primary" },
                { key: "inventory", color: "dark" },
            ],
            mode: "receipt",
            stockForm: { productId: null, amount: null, note: "" },
            conversionForm: { outputId: null, count: null },
            saving: false,
            message: null,
            error: false,
        };
    },
    computed: {
        currentMode() {
            return this.modes.find((m) => m.key === this.mode);
        },
        selectedStock() {
            return this.stock.find((item) => item.product.id === this.stockForm.productId) || null;
        },
        stockAmount() {
            return this.parseAmount(this.stockForm.amount);
        },
        stockValid() {
            const amount = this.stockAmount;
            if (!this.stockForm.productId || !Number.isFinite(amount)) return false;
            return this.mode === "inventory" ? amount >= 0 : amount > 0;
        },
        newAmount() {
            const change = this.mode === "receipt" ? this.stockAmount : -this.stockAmount;
            return this.selectedStock.amount + change;
        },
        selectedConversion() {
            return this.conversions.find((c) => c.output_product === this.conversionForm.outputId) || null;
        },
        conversionCount() {
            return this.parseAmount(this.conversionForm.count);
        },
        conversionValid() {
            return this.selectedConversion && Number.isFinite(this.conversionCount) && this.conversionCount > 0;
        },
        consumedAmount() {
            return Math.round((this.conversionCount / this.selectedConversion.coeficient) * 100) / 100;
        },
        sourceAfter() {
            const source = this.stock.find((item) => item.product.id === this.selectedConversion.source_product);
            return (source ? source.amount : 0) - this.consumedAmount;
        },
    },
    methods: {
        setMode(mode) {
            this.mode = mode;
            this.message = null;
        },
        selectProduct(productId) {
            if (this.mode === "conversion") {
                this.mode = "receipt";
            }
            this.stockForm.productId = productId;
            this.message = null;
            this.$el.scrollIntoView({ behavior: "smooth" });
        },
        signed(number) {
            return (number > 0 ? "+" : "") + this.formatAmount(number);
        },
        submitStock() {
            this.saving = true;
            this.$store.getters.api
                .put("/update_stock/" + this.warehouseId + "/" + this.stockForm.productId, {
                    change_type: this.mode,
                    amount: this.stockAmount,
                    note: this.stockForm.note,
                })
                .then(() => {
                    this.notify(false, this.$t("warehouses.entrySaved"));
                    this.stockForm.amount = null;
                    this.stockForm.note = "";
                    this.$emit("updated");
                })
                .catch(() => this.notify(true, this.$t("warehouses.entryError")))
                .finally(() => (this.saving = false));
        },
        submitConversion() {
            this.saving = true;
            this.$store.getters.api
                .put("/warehouse/" + this.warehouseId + "/product_conversion/submit", {
                    selectedProduct: this.conversionForm.outputId,
                    count: this.conversionCount,
                })
                .then(() => {
                    this.notify(false, this.$t("warehouses.entrySaved"));
                    this.conversionForm.count = null;
                    this.$emit("updated");
                })
                .catch(() => this.notify(true, this.$t("warehouses.entryError")))
                .finally(() => (this.saving = false));
        },
        notify(isError, msg) {
            this.error = isError;
            this.message = msg;
        },
    },
};
</script>
